import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const LandValuation = () => {
  // State variables for land valuation calculation
  const [area, setArea] = useState(0); // Area of the land
  const [marketPrice, setMarketPrice] = useState(0); // Market price per unit area
  const [landValue, setLandValue] = useState(0); // Calculated land value
  const [areaUnit, setAreaUnit] = useState("hectares"); // Area unit (hectares, acres, square meters, square feet)
  const [priceUnit, setPriceUnit] = useState("per hectare"); // Price per unit area (per hectare, per acre, per square meter)

  // Event handlers to update state values
  const handleAreaChange = (e) => setArea(parseFloat(e.target.value) || 0);
  const handleMarketPriceChange = (e) =>
    setMarketPrice(parseFloat(e.target.value) || 0);

  // Event handlers for unit selection
  const handleAreaUnitChange = (e) => setAreaUnit(e.target.value);
  const handlePriceUnitChange = (e) => setPriceUnit(e.target.value);

  // Conversion functions for area and market price to standard units
  const convertArea = (area, unit) => {
    switch (unit) {
      case "acres":
        return area * 2.471; // 1 hectare = 2.471 acres
      case "square meters":
        return area * 10000; // 1 hectare = 10,000 square meters
      case "square feet":
        return area * 107639; // 1 hectare = 107,639 square feet
      default:
        return area; // Already in hectares
    }
  };

  const convertMarketPrice = (price, unit) => {
    switch (unit) {
      case "per acre":
        return price / 2.471; // Convert price per acre to price per hectare
      case "per square meter":
        return price / 10000; // Convert price per square meter to price per hectare
      case "per square foot":
        return price / 107639; // Convert price per square foot to price per hectare
      default:
        return price; // Already per hectare
    }
  };

  // useEffect hook to calculate land value when any input changes
  useEffect(() => {
    // Convert to standard units before calculating land value
    const areaInHectares = convertArea(area, areaUnit);
    const marketPriceInHectares = convertMarketPrice(marketPrice, priceUnit);

    // Calculate Land Value = Area x Market Price per Unit Area
    if (areaInHectares > 0 && marketPriceInHectares > 0) {
      const calculatedLandValue = areaInHectares * marketPriceInHectares;
      setLandValue(calculatedLandValue);
    } else {
      setLandValue(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [area, marketPrice, areaUnit, priceUnit]);

  return (
    <Layout title="Land Valuation Calculator" path="land-valuation-calculator">
      <section className="section-padding" id="land-valuation-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Land Valuation Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Land Valuation Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Area Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="area" className="form-label">
                        Area
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Area:</strong> Enter the total area of the land. Use the dropdown to specify the unit of measurement."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="area"
                          placeholder="Enter area"
                          value={area || ""}
                          onChange={handleAreaChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={areaUnit}
                          onChange={handleAreaUnitChange}
                        >
                          <option value="hectares">hectares</option>
                          <option value="acres">acres</option>
                          <option value="square meters">square meters</option>
                          <option value="square feet">square feet</option>
                        </select>
                      </div>
                    </div>

                    {/* Market Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="marketPrice" className="form-label">
                        Market Price per Unit Area
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Market Price per Unit Area:</strong> Enter the market value per unit of area. Use the dropdown to specify the pricing unit."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <div className="d-flex">
                        <input
                          type="number"
                          className="form-control"
                          id="marketPrice"
                          placeholder="Enter market price"
                          value={marketPrice || ""}
                          onChange={handleMarketPriceChange}
                        />
                        <select
                          className="form-select ms-2"
                          value={priceUnit}
                          onChange={handlePriceUnitChange}
                        >
                          <option value="per hectare">per hectare</option>
                          <option value="per acre">per acre</option>
                          <option value="per square meter">
                            per square meter
                          </option>
                          <option value="per square foot">
                            per square foot
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Land Value:{" "}
                        <span className="fw-bold ms-3">
                          {landValue === 0
                            ? "0.00"
                            : landValue.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Land Valuation Calculator
                </h3>

                {/* What is a Land Valuation Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Land Valuation Calculator?
                </h4>
                <p>
                  The **Land Valuation Calculator** helps individuals, real
                  estate investors, and developers estimate the value of a piece
                  of land based on its area and current market prices. By
                  multiplying the land area by the price per unit area, this
                  tool provides a quick and accurate land value estimate.
                </p>

                {/* Why Use a Land Valuation Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Land Valuation Calculator?
                </h4>
                <p>
                  Land valuation is essential when buying, selling, or
                  appraising land. This calculator helps investors and buyers
                  assess whether the asking price is reasonable, assists in tax
                  assessments, and facilitates property sales. It is an
                  indispensable tool for real estate professionals and
                  individuals interested in land investments.
                </p>

                {/* Benefits of Using a Land Valuation Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Land Valuation Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps investors and buyers assess land value:
                    </strong>{" "}
                    This calculator allows you to quickly determine whether the
                    price of land is fair based on its area and market price.
                  </li>
                  <li>
                    <strong>Useful for real estate appraisal:</strong> Real
                    estate appraisers can use this tool to determine the market
                    value of land properties.
                  </li>
                  <li>
                    <strong>
                      Assists in tax assessments and property sales:
                    </strong>{" "}
                    It is helpful for calculating property taxes and determining
                    the value of land when selling or transferring ownership.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Land Valuation Calculator?
                </h4>
                <p>The Land Valuation Calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Real estate professionals:</strong> For appraising
                    properties and assessing land value.
                  </li>
                  <li>
                    <strong>Land developers:</strong> To estimate land value
                    when planning new developments.
                  </li>
                  <li>
                    <strong>Appraisers:</strong> For providing land value
                    assessments in various contexts, such as property sales or
                    legal disputes.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How is Land Value Determined?</h4>
                <p>
                  Land value is typically determined by the location, size,
                  market demand, and other external factors. Using market price
                  per unit area as a baseline, the area of land is multiplied by
                  this rate to estimate its value. Factors like zoning, land
                  use, and nearby infrastructure can also impact land value.
                </p>

                <h4 className="mt-4 mb-3">Can Land Value Fluctuate?</h4>
                <p>
                  Yes, land value can fluctuate based on market conditions,
                  demand, location, and other economic factors. Understanding
                  market trends and local conditions can help investors
                  anticipate changes in land value.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandValuation;
